import { CSSProperties, ReactNode, useEffect, useState } from 'react';

import { useDeviceType } from '@braskai/ui-kit';

import { Box, ClickAwayListener, Stack, TooltipProps } from '@mui/material';

import { CloseButton } from './CloseButton';
import { Content } from './Content';
import { Description } from './Description';
import { Image } from './Image';
import { Link } from './Link';
import { SContent, STooltip } from './styled';
import { Title } from './Title';

export type HoverCardProps = {
  children: ReactNode;
  content: ReactNode;
  cursor?: CSSProperties['cursor'];
  isOpen?: boolean;
  placement?: TooltipProps['placement'];
  placementMobile?: TooltipProps['placement'];
  width?: CSSProperties['width'];
  handleClose?: () => void;
  handleOpen?: () => void;
  onTooltipOpen?: () => void;
};

export const HoverCard = ({
  children,
  content,
  cursor = 'default',
  isOpen,
  placement = 'right',
  placementMobile = 'bottom',
  width,
  handleClose,
  handleOpen,
  onTooltipOpen,
}: HoverCardProps) => {
  const { isMobile, isTablet } = useDeviceType();
  const isMobileOrTablet = isMobile || isTablet;

  const isControlled = isOpen !== undefined;

  const [isOpenPopover, setIsOpenPopover] = useState(false);

  const onToggle = () => {
    setIsOpenPopover((prevState) => !prevState);

    if (isOpenPopover) {
      handleClose?.();
    } else {
      handleOpen?.();
    }
  };

  const onOpen = () => {
    setIsOpenPopover(true);
    onTooltipOpen?.();
    handleOpen?.();
  };

  const onClose = () => {
    setIsOpenPopover(false);
    handleClose?.();
  };

  useEffect(() => {
    if (isControlled) {
      setIsOpenPopover(isOpen);
    }
  }, [isControlled, isOpen]);

  if (isMobileOrTablet) {
    return (
      <ClickAwayListener onClickAway={onClose}>
        <Stack direction="row">
          <STooltip
            title={<SContent>{content}</SContent>}
            open={isControlled ? isOpen : isOpenPopover}
            placement={placementMobile}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={onClose}
          >
            <Box sx={{ cursor, width }} onClick={onToggle}>
              {children}
            </Box>
          </STooltip>
        </Stack>
      </ClickAwayListener>
    );
  }

  return (
    <Stack direction="row">
      <STooltip
        title={<SContent>{content}</SContent>}
        disableFocusListener={isControlled}
        disableHoverListener={isControlled}
        disableTouchListener={isControlled}
        placement={placement}
        arrow
        {...(isControlled ? { open: isOpen, onClose, onOpen } : {})}
      >
        <Box sx={{ cursor, width }} onClick={isControlled ? onToggle : undefined}>
          {children}
        </Box>
      </STooltip>
    </Stack>
  );
};

HoverCard.Content = Content;
HoverCard.Title = Title;
HoverCard.Description = Description;
HoverCard.Link = Link;
HoverCard.Image = Image;
HoverCard.CloseButton = CloseButton;
